import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { checkAccess } from '../../api/mobileapi';
import { AccessPointData } from '../../api/types';
import { AdamsError } from '../../utils/errors';
import { hasNFCSupport, isProductionEnvironment } from '../../utils/utils';
import { HideInProduction } from '../HideInProduction/HideInProduction';
import { NFCNoSupport } from '../NFC/NFCNoSupport';
import { NFCDevEnvInfo } from '../NFCDevEnvInfo/NFCDevEnvInfo';
import { EmulateAccessPointStepIndicator } from './EmulateAccessPointStepIndicator';
import { EmulateAccessPointStep1 } from './Steps/EmulateAccessPointStep1';
import { EmulateAccessPointStep2 } from './Steps/EmulateAccessPointStep2';

interface EmulateAccessPointProps {
    translations: Record<string, string>;
}

export const EmulateAccessPoint: React.FC<EmulateAccessPointProps> = ({ translations }) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [accessInformation, setAccessInformation] = useState<AccessPointData | null>(null);
    const [nfcData, setNfcData] = useState<AccessPointData | null>(null);
    const [loading, setLoading] = useState(false);
    const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);

    const { mutate: checkAccessMutate } = useMutation(checkAccess, {
        onSuccess: (data) => {
            setCurrentStep(1);
            setLoading(false);
            setAccessInformation(data);
        },
        onError: (error: AdamsError) => {
            setScanErrorMessage(error?.response?.data?.error || error.message);
            setLoading(false);
        },
    });

    const resetToStep = (step: number) => {
        setCurrentStep(step);
    };

    const resetStep1 = () => {
        resetToStep(0);
        setAccessInformation(null);
    };

    const resetStep2 = () => {
        resetToStep(1);
        setNfcData(null);
    };

    const noNFCSupport = !hasNFCSupport();
    const displayStep2 = currentStep > 0 && accessInformation;

    if (isProductionEnvironment() && noNFCSupport) {
        return <NFCNoSupport translations={translations} />;
    }

    return (
        <>
            {noNFCSupport && (
                <Box mb={2}>
                    <NFCNoSupport translations={translations} />

                    <HideInProduction extraParams={noNFCSupport}>
                        <NFCDevEnvInfo />
                    </HideInProduction>
                </Box>
            )}

            <Card>
                <CardContent>
                    {!nfcData && (
                        <EmulateAccessPointStepIndicator
                            currentStep={currentStep}
                            onReset={resetStep1}
                            translations={translations}
                        />
                    )}

                    {currentStep === 0 && (
                        <EmulateAccessPointStep1
                            checkAccess={checkAccessMutate}
                            loading={loading}
                            scanError={scanErrorMessage}
                            setLoading={setLoading}
                            setScanError={setScanErrorMessage}
                            translations={translations}
                        />
                    )}

                    {displayStep2 && (
                        <EmulateAccessPointStep2
                            accessInformation={accessInformation!}
                            nfcData={nfcData}
                            onResetStep2={resetStep2}
                            setNfcData={setNfcData}
                            translations={translations}
                        />
                    )}
                </CardContent>
            </Card>
        </>
    );
};
